import { useState, useMemo, useCallback, useEffect } from "react";
import { useDispatch } from 'react-redux';
import { Box, Typography } from "@mui/material";
import PropTypes from "prop-types";
import { DataGridPro, useGridApiRef } from "@mui/x-data-grid-pro";
import { SkeletonDataGrid } from '../../../../reusable-components/skeleton/SkeletonDataGrid';
import { filterRows, getGridFilterFieldValues, GetGridKeys, loadSavedFilters, loadSavedSorts, onSortModelChange } from '../../../../reusable-components/datagrid/dataGridHelper';
import { createColumns, CustomFilter } from "../../../../reusable-components/datagrid/custom-filters";
import { ALL_PRODUCTS } from '../../../../reusable-components/datagrid/orderGuidesColumns';
import { useSelector } from '../../../../redux/store';
import { removeUserSettings } from '../../../../redux/slices/user';
import { useFilterModel } from "../../../../hooks/useFilterModel";
import CustomToolbar from "./CustomToolbar";
import DataGridPersistSettings from "../../../../reusable-components/datagrid/DataGridPersistSettings";
import DeactivatedTooltipRow from "../../../../reusable-components/datagrid/DeactivatedTooltipRow";

OrderGuideActiveItemsGrid.propTypes = {
  data: PropTypes.object,
  tempOrderGuide: PropTypes.array,
  setTempOrderGuide: PropTypes.func,
  handleUpdateOrderGuide: PropTypes.func,
  active: PropTypes.bool,
  setActive: PropTypes.func,
  currentOrderGuide: PropTypes.object,
  quickFilter: PropTypes.string,
  setQuickFilter: PropTypes.func,
};
export default function OrderGuideActiveItemsGrid({
  data, tempOrderGuide, setTempOrderGuide, handleUpdateOrderGuide, active, setActive, currentOrderGuide, quickFilter, setQuickFilter
}) {
  const gridId = 'order-guide-active-items';
  const gridRef = useGridApiRef();
  const { filterStorageKey, sortStorageKey, gridKey, baseSearchTextStorageKey } = GetGridKeys(gridId);
  const { isLoading, isLoadingProducts } = useSelector((state) => state.orderGuides);
  const [isOrderGuidExpanded, setIsOrderGuidExpanded] = useState(false);
  const [filters, setFilters] = useState(loadSavedFilters(filterStorageKey));
  const filterModel = useFilterModel(filters);
  const [anchorEl, setAnchorEl] = useState(null);
  const [activeFilter, setActiveFilter] = useState(null);
  const [isSelecting, setIsSelecting] = useState(false);
  const [selectedRowIds, setSelectedRowIds] = useState([]);
  const [columnsState, setColumnsState] = useState();
  const handleSelectionModelChange = (selection) => setSelectedRowIds(selection);
  const columnReorder = (colState) => {
    try {
      const updatedColumns = colState?.columns
        ? ALL_PRODUCTS
          .map((col) => ({ ...col, ...colState?.columns?.dimensions[col.field] }))
          .sort((a, b) => {
            const indexA = colState?.columns?.orderedFields?.indexOf(a.field);
            const indexB = colState?.columns?.orderedFields?.indexOf(b.field);
            if (indexA > -1 && indexB > -1) return indexA - indexB;
            return 0;
          })
        : ALL_PRODUCTS;
      return updatedColumns;
    } catch (err) {
      return ALL_PRODUCTS;
    }
  };
  const activeColumns = useMemo(() => createColumns(columnReorder(columnsState), filters, setAnchorEl, setActiveFilter, gridId, activeFilter),
    [activeFilter, filters, gridId, columnsState]);
  const [sortModel, setSortModel] = useState(loadSavedSorts(sortStorageKey));

  const { gridPersists, gridSettings, updateGridSettings } = DataGridPersistSettings({ gridId, columns: activeColumns });
  const dispatch = useDispatch();

  const initialState = useMemo(() => gridSettings?.gridState || {}, [gridSettings]);

  const setColumnWidths = () => {
    const currentState = gridRef?.current?.exportState();
    if (gridPersists) updateGridSettings(currentState);
    setColumnsState(currentState);
  };

  const filteredRows = useMemo(() => filterRows(data.activeData, quickFilter, activeColumns), [data.activeData, quickFilter, activeColumns]);

  const fieldValues = useMemo(() => getGridFilterFieldValues(activeColumns, filteredRows, filters), [filteredRows, filters, activeColumns]);

  const handleRemoveSelectedRows = useCallback(() => {
    const newOrderGuide = tempOrderGuide?.filter((item) => !selectedRowIds.includes(item.productId));
    setTempOrderGuide(newOrderGuide);
    handleUpdateOrderGuide(newOrderGuide);
    handleSelectClick();
  }, [tempOrderGuide, selectedRowIds, handleUpdateOrderGuide]);

  const handleSelectClick = useCallback(() => {
    setSelectedRowIds([]);
    setIsSelecting((prev) => !prev);
  }, []);

    useEffect(() => {
        const currentState =  gridRef?.current?.exportState();
        setColumnsState(currentState);
    }, [gridRef]);
    const columnVisibilityModel = columnsState?.columns?.columnVisibilityModel ?? initialState.columns?.columnVisibilityModel ?? {};

    const handleGridVisibilityChange = (model) => {
        const newState = { ...columnsState, columns: { ...columnsState?.columns, columnVisibilityModel: model } };
        setColumnsState(newState);
        if (gridPersists) updateGridSettings(newState);
    };

    const handleGridOrderChange = () => {
        const currentState = gridRef?.current?.exportState();
      if (gridPersists) updateGridSettings(currentState);
      setColumnsState(currentState);
    };

    const resetGridState = () => {
        // remove settings from database and localstorage
        updateGridSettings.cancel();
        localStorage.removeItem(gridKey);
        localStorage.removeItem(filterStorageKey);
        localStorage.removeItem(sortStorageKey);
        columnsState?.columns?.orderedFields?.forEach((c) => localStorage.removeItem(`${baseSearchTextStorageKey}-${c}`));
        setFilters({});
        setSortModel([]);
        setColumnsState({
          columns: {
            columnVisibilityModel: {},
            dimensions: {},
            orderedFields: ALL_PRODUCTS.map((c) => c.field),
          },
        });
        if (gridSettings) dispatch(removeUserSettings([gridKey]));
    };

    return <>
        <CustomToolbar
            setFilters={setFilters}
            setActiveFilter={setActiveFilter}
            setQuickFilter={setQuickFilter}
            isSelecting={isSelecting}
            active={active}
            setActive={setActive}
            currentOrderGuide={currentOrderGuide}
            onRemoveSelectedRows={handleRemoveSelectedRows}
            onSelectClick={handleSelectClick}
            columnsState={columnsState}
            setColumnsState={setColumnsState}
            resetGridState={resetGridState}
            handleGridSettingsUpdate={updateGridSettings}
            gridColumns={activeColumns}
            gridId={gridId}
        />
    <DataGridPro
      initialState={initialState}
      getRowHeight={() => 52}
      rows={filteredRows}
      getRowId={(row) => row.id}
      getRowClassName={(params) => (params.row.deactivated ? 'deactivated-row' : '')}
      disableRowSelectionOnClick
      loading={isLoading || isLoadingProducts}
      sortModel={sortModel}
      apiRef={gridRef}
      onSortModelChange={(model) => onSortModelChange(setSortModel, model, sortStorageKey)}
      componentsProps={{
        loadingOverlay: { height: 100}
      }}
      components={{
        LoadingOverlay: SkeletonDataGrid,
        Row: DeactivatedTooltipRow,
        NoRowsOverlay: () => (
          <Box sx={{ p: 2, textAlign: 'center', width: '100%' }}>
            <Typography variant="subtitle2" color="text.secondary">
              No rows
            </Typography>
          </Box>
        ),
      }}
      filterModel={filterModel}
      disableColumnMenu
      disableColumnSelector
      disableColumnPinning
      disableColumnFilter
      checkboxSelection={isSelecting}
      rowSelectionModel={selectedRowIds}
      columnVisibilityModel={columnVisibilityModel}
      onColumnVisibilityModelChange={handleGridVisibilityChange}
      onColumnOrderChange={handleGridOrderChange}
      onColumnWidthChange={setColumnWidths }
      columns={activeColumns}
      onRowSelectionModelChange={handleSelectionModelChange}
      sx={{
        height: !filteredRows.length ? 150 : 'auto',
        maxHeight: isOrderGuidExpanded ? 'calc(100vh - 150px)' : '33vh',
        width: '100%',
        '& .MuiDataGrid-columnHeaders': {
          position: 'sticky',
          zIndex: 999,
        },
        '& .deactivated-row': {
          color: 'rgba(0, 0, 0, 0.65)',
          backgroundColor: 'rgba(0, 0, 0, 0.12)',
          '&:hover': {
            backgroundColor: 'rgba(0, 0, 0, 0.12)',
            color: 'rgba(0, 0, 0, 0.65)',
          },
        },
      }}
    />
    <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
      {filteredRows.length > 0 && <Typography
        onClick={() => setIsOrderGuidExpanded(!isOrderGuidExpanded)}
        color="info.main"
        sx={{textDecoration: 'underline', cursor: 'pointer'}}
        variant="caption"
      >
        {isOrderGuidExpanded ? 'Collapse' : 'Expand'}
      </Typography>}
      <Typography variant="caption" color="text.secondary">
        Total: {filteredRows.length}
      </Typography>
    </Box>
    <CustomFilter
      gridId={gridId}
      anchorEl={anchorEl}
      setAnchorEl={setAnchorEl}
      setActiveFilter={setActiveFilter}
      activeFilter={activeFilter}
      filters={filters}
      setFilters={setFilters}
      fieldValues={fieldValues}
      columns={activeColumns}
    />
  </>
}
